// src/auth/AuthProvider.tsx
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";

import { API } from "../api/api";

const TOKEN_KEY = "tv_token";

// Idle-session timeout (COND-01/COND-02): log the user out after this many
// minutes of no interaction. Per-tenant configurable via REACT_APP_IDLE_TIMEOUT_MINUTES
// (baked at build time by deploy-frontend.sh). Default 30 min; 0 disables.
const IDLE_TIMEOUT_MIN = Number(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES ?? "30");

type ResultAccessLevel = 'full' | 'summary' | 'none';

interface AuthCtx {
  token: string | null;
  username: string | null;
  password: string | null;
  isAdmin: boolean;
  resultAccessLevel: ResultAccessLevel;
  isPartner: boolean;
  // MFA state
  mfaToken: string | null;
  requires2fa: boolean;
  mfaSetupRequired: boolean;
  // Current customer ID - can be from JWT or dev override
  customerId: string | null;
  customerName: string | null;
  // For development only
  devOverrides?: {
    customerId?: string;
    username?: string;
  };
  setDevOverrides?: (overrides: { customerId?: string; username?: string; userRole?: 'admin' | 'user' }) => void;
  loginWithPassword: (u: string, p: string) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  loginWithAmazon: () => Promise<void>;
  handleAmazonCallback: (code: string, state: string) => Promise<void>;
  registerWithPassword: (email: string, password: string, fullName?: string, companyName?: string) => Promise<void>;
  verifyMfa: (code: string, isRecovery?: boolean) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  isTokenValid: (token: string | null) => boolean;
  toggleAdminRole?: () => void; // Dev only function
}

const Ctx = createContext<AuthCtx>(null as any);
export const useAuth = () => useContext(Ctx);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [username, setUsername] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [requires2fa, setRequires2fa] = useState(false);
  // Initialize isAdmin and resultAccessLevel from stored token on page load
  const [isAdmin, setIsAdmin] = useState<boolean>(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) return false;
    try {
      const decoded = jwtDecode<{ is_admin: boolean }>(storedToken);
      return Boolean(decoded.is_admin);
    } catch {
      return false;
    }
  });
  const [resultAccessLevel, setResultAccessLevel] = useState<ResultAccessLevel>(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) return 'full';
    try {
      const decoded = jwtDecode<{ result_access_level?: string }>(storedToken);
      return (decoded.result_access_level as ResultAccessLevel) || 'full';
    } catch {
      return 'full';
    }
  });
  // New state for development overrides
  const [devOverrides, setDevOverridesState] = useState<{
    customerId?: string;
    username?: string;
    userRole?: 'admin' | 'user'
  }>({});

  const [isPartner, setIsPartner] = useState<boolean>(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) return false;
    try {
      const decoded = jwtDecode<{ is_partner?: boolean; result_access_level?: string }>(storedToken);
      // Decode from JWT, fallback to summary access level check for backwards compat
      return Boolean(decoded.is_partner) || decoded.result_access_level === 'summary';
    } catch {
      return false;
    }
  });

  // Derive mfaSetupRequired from the token
  const getMfaSetupRequired = (): boolean => {
    if (!token) return false;
    try {
      const decoded = jwtDecode<{ mfa_setup_required?: boolean }>(token);
      return Boolean(decoded.mfa_setup_required);
    } catch {
      return false;
    }
  };

  const saveToken = (t: string) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);

    // Pick role and access level out of the JWT we already receive
    try {
      const decoded = jwtDecode<{ is_admin: boolean; result_access_level?: string; is_partner?: boolean }>(t);
      setIsAdmin(Boolean(decoded.is_admin));
      setResultAccessLevel((decoded.result_access_level as ResultAccessLevel) || 'full');
      setIsPartner(Boolean(decoded.is_partner) || decoded.result_access_level === 'summary');
    } catch {
      setIsAdmin(false);
      setResultAccessLevel('full');
      setIsPartner(false);
    }
  };

  // Extract customer ID from token or use dev override
  const getCustomerId = (): string | null => {
    // In development with override, use that
    if (process.env.NODE_ENV === 'development' && devOverrides.customerId) {
      return devOverrides.customerId;
    }

    // Otherwise extract from token
    if (!token) return null;

    try {
      const decoded = jwtDecode<{ customer_id?: string }>(token);
      return decoded.customer_id || null;
    } catch {
      return null;
    }
  };

  const getCustomerName = (): string | null => {
    if (!token) return null;
    try {
      const decoded = jwtDecode<{ customer_name?: string }>(token);
      return decoded.customer_name || null;
    } catch {
      return null;
    }
  };

  // Development-only overrides setter
  const setDevOverrides = process.env.NODE_ENV === 'development'
    ? (overrides: { customerId?: string; username?: string; userRole?: 'admin' | 'user' }) => {
      setDevOverridesState(prev => ({
        ...prev,
        ...overrides
      }));

      // Handle role changes
      if (overrides.userRole) {
        setIsAdmin(overrides.userRole === 'admin');
      }
    }
    : undefined;

  // For development only: toggle between admin and normal user
  const toggleAdminRole = process.env.NODE_ENV === 'development'
    ? () => {
      const newIsAdmin = !isAdmin;
      setIsAdmin(newIsAdmin);
      setDevOverridesState(prev => ({
        ...prev,
        userRole: newIsAdmin ? 'admin' : 'user'
      }));
    }
    : undefined;

  const loginWithPassword = async (u: string, p: string) => {
    const res = await fetch(`${API}/auth/login/access-token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ username: u, password: p }),
    });
    if (!res.ok) throw new Error(await res.text());

    const data = await res.json();

    // 2FA challenge — don't save a real token yet
    if (data.requires_2fa) {
      setMfaToken(data.mfa_token);
      setRequires2fa(true);
      setUsername(u);
      setPassword(p);
      return;
    }

    saveToken(data.access_token);
    setUsername(u);
    setPassword(p);               // stored only in memory, never in localStorage
  };

  const registerWithPassword = async (email: string, pw: string, fullName?: string, companyName?: string) => {
    const res = await fetch(`${API}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password: pw, full_name: fullName, company_name: companyName }),
    });
    if (!res.ok) throw new Error(await res.text());

    saveToken((await res.json()).access_token);
    setUsername(email);
    setPassword(pw);
  };

  const loginWithGoogle = async (googleIdToken: string) => {
    const res = await fetch(`${API}/auth/login/oidc`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ provider: "google", id_token: googleIdToken }),
    });
    if (!res.ok) throw new Error(await res.text());

    const data = await res.json();

    if (data.requires_2fa) {
      setMfaToken(data.mfa_token);
      setRequires2fa(true);
      setUsername(null);
      setPassword(null);
      return;
    }

    saveToken(data.access_token);
    setUsername(null);
    setPassword(null);
  };

  const loginWithAmazon = async () => {
    // Step 1: Get authorization URL from our backend
    const res = await fetch(`${API}/auth/login/amazon/authorize`);
    if (!res.ok) throw new Error(await res.text());
    const data = await res.json();

    // Store state_token for the callback
    sessionStorage.setItem("amazon_state_token", data.state_token);

    // Step 2: Redirect to Amazon's login page
    window.location.href = data.authorization_url;
  };

  const handleAmazonCallback = async (code: string, state: string) => {
    const stateToken = sessionStorage.getItem("amazon_state_token");
    if (!stateToken) throw new Error("Missing Amazon state token");
    sessionStorage.removeItem("amazon_state_token");

    const res = await fetch(`${API}/auth/login/amazon/callback`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, state, state_token: stateToken }),
    });
    if (!res.ok) throw new Error(await res.text());

    const data = await res.json();

    if (data.requires_2fa) {
      setMfaToken(data.mfa_token);
      setRequires2fa(true);
      return;
    }

    saveToken(data.access_token);
  };

  const verifyMfa = async (code: string, isRecovery = false) => {
    if (!mfaToken) throw new Error("No MFA session active");

    const res = await fetch(`${API}/auth/login/verify-2fa`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        mfa_token: mfaToken,
        totp_code: isRecovery ? null : code,
        recovery_code: isRecovery ? code : null,
      }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "MFA verification failed");

    const data = await res.json();
    saveToken(data.access_token);
    setMfaToken(null);
    setRequires2fa(false);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUsername(null);
    setPassword(null);
    setMfaToken(null);
    setRequires2fa(false);
    setIsAdmin(false);
    setResultAccessLevel('full');
    setIsPartner(false);
  };

  // Add a function to refresh the token
  const refreshToken = async () => {
    // Only try refreshing if we have credentials
    if (!username || !password) return false;

    try {
      const res = await fetch(`${API}/auth/login/access-token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ username, password }),
      });

      if (!res.ok) return false;

      saveToken((await res.json()).access_token);
      return true;
    } catch (error) {
      console.error("Failed to refresh token:", error);
      return false;
    }
  };

  const isTokenValid = (token: string | null): boolean => {
    if (!token) return false;

    try {
      const decoded = jwtDecode<{ exp: number }>(token);
      // Check if token is expired (exp is in seconds)
      return decoded.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  };

  // Idle-session timeout: while logged in, reset a timer on user activity and
  // log out after IDLE_TIMEOUT_MIN minutes of inactivity (COND-01/COND-02).
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!token || IDLE_TIMEOUT_MIN <= 0) return;

    const reset = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        logout();
      }, IDLE_TIMEOUT_MIN * 60 * 1000);
    };

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Add the refreshToken function to your context value
  return (
    <Ctx.Provider value={{
      token,
      username,
      password,
      isAdmin,
      resultAccessLevel,
      isPartner,
      mfaToken,
      requires2fa,
      mfaSetupRequired: getMfaSetupRequired(),
      customerId: getCustomerId(),
      customerName: getCustomerName(),
      devOverrides: process.env.NODE_ENV === 'development' ? devOverrides : undefined,
      setDevOverrides,
      loginWithPassword,
      loginWithGoogle,
      loginWithAmazon,
      handleAmazonCallback,
      registerWithPassword,
      verifyMfa,
      logout,
      refreshToken,
      isTokenValid,
      toggleAdminRole
    }}>
      {children}
    </Ctx.Provider>
  );
};
